import Big from 'big.js';
import { DateTime } from 'luxon';

import { CampaignChannels } from 'domains/campaigns/types';
import { MergeTag } from 'domains/content/types';
import { Cohort, Segment } from 'domains/segments/types';
import { Contact, CuredApiError, ErrorCodes, ContactColumn } from 'models/types';

import type { AccessCentrePermission, AccessCentreRole } from 'domains/accessCentre/types';

/** Debounce delay (ms) applied to all table search inputs. Change here to update all at once. */
export const SEARCH_DEBOUNCE_MS = 500;

/**
 * Truncates a string to a maximum length, appending `...` when shortened.
 * For table cells that should ellipsis by layout (and tooltip on hover), use `TruncatedTextWithTooltip`
 * from `domains/core/components/TruncatedTextWithTooltip`.
 */
export const truncateStringWithEllipsis = (value: string, maxLength: number): string => {
    if (maxLength < 4) {
        return value;
    }
    if (!value || value.length <= maxLength) {
        return value;
    }
    return `${value.slice(0, maxLength - 3)}...`;
};

/**
 * Gets the greeting corresponding with the time of day.
 * 12:00 AM - 11:59 AM = good morning
 * 12:00 PM - 4:59 PM = good afternoon
 * 5:00 PM - 11:59 PM = good evening
 * @returns Greeting string
 */
export const getTimeDependentGreeting = (date: Date): string => {
    const hours = date.getHours();
    if (hours >= 0 && hours < 12) {
        return 'good morning';
    } else if (hours >= 12 && hours < 17) {
        return 'good afternoon';
    } else {
        return 'good evening';
    }
};

/**
 * Returns a date with the following format: Tuesday, October 26, 2021.
 * @param date
 * @returns A user friendly date string.
 */
export const getUserFriendlyDate = (date: Date): string => {
    const options: Intl.DateTimeFormatOptions = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return date.toLocaleDateString('en-US', options);
};

/**
 * Returns the first two characters of an email string (e.g. for avatar initials).
 * Callers apply casing as needed: .toUpperCase() for display, .toLowerCase() for activity formatters.
 */
export const getInitialsFromEmail = (email: string): string => email.slice(0, 2);

/**
 * Returns numbers with commas e.g. 1000 -> 1,000
 *
 * @param num The number that will be converted.
 */
export const getNumberWithCommas = (num: number) => {
    if (isNaN(num)) {
        num = 0;
    }
    return Math.floor(num)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

/**
 * Formats the address of the contact.
 * @param contact The contact whose address will be formatted.
 * @returns The formatted address.
 */
export const getFormattedAddress = (contact: Contact) => {
    const addressFields = [contact.street_address, contact.street_address_2, contact.zip_code];
    return addressFields.filter((field) => !!field).join(', ');
};

export const getFormattedDate = (date: string): string => DateTime.fromISO(date).toUTC().toFormat('LLL dd, yyyy');
export const getFormattedLocalDate = (date: string): string =>
    DateTime.fromISO(date).setLocale('en-US').toLocaleString(DateTime.DATE_MED);

/**
 * Returns the user's respective timezone.
 * @returns
 */
export const getTimezone = (): string => {
    let options: Intl.DateTimeFormatOptions = {
        timeZoneName: 'short',
    };
    const timezoneArray = new Date().toLocaleTimeString('en-US', options).split(/\s/);
    // Get the last element in the array. Different browsers will return a different number of elements.
    // In case the timezone is not found, return undefined instead of crashing.
    return timezoneArray[timezoneArray.length - 1] ?? '';
};

export const getFormattedLocalTime = (time: string): string => {
    const dateTime = DateTime.fromISO(time).setLocale('en-US');
    const formattedTime = dateTime.toFormat('t');
    const timezone = getTimezone();
    return `${formattedTime} ${timezone}`;
};

export const getAgeFromBirthday = (birthday?: string): number => {
    if (!birthday) return;

    var today = new Date();
    var birthDate = new Date(birthday);

    if (birthDate.getTime() === new Date(0).getTime()) {
        return null;
    }
    var age = today.getFullYear() - birthDate.getFullYear();
    var month = today.getMonth() - birthDate.getMonth();
    if (month < 0 || (month === 0 && today.getDate() < birthDate.getDate())) {
        age--;
    }
    return age;
};

/**
 * Formats a ten digit phone number to the following format: (123) 456-7890
 * @param phone The (10 digit/US) phone number whose string will be formatted.
 * @returns The formatted phone number.
 */
export const getFormattedPhoneNumber = (phone: string): string => {
    if (!phone) {
        return '';
    }
    const formattedPhone = phone.replace(/[^\d]/g, '');

    // Check if US phone number
    if (formattedPhone.length === 10) {
        return formattedPhone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
    } else {
        return phone;
    }
};

/**
 * Finds an audience id given the audience name
 * @param name the audience name to look for
 * @param audienceList an array of audience objects
 * @returns an id as an integer
 */
export const getAudienceIdFromName = (name: string, audienceList: Segment[]): number => {
    const audienceObj = audienceList.find((audience) => audience.name === name);
    return audienceObj.id;
};

/**
 * Creates and returns an error with the corresponding error message, or the default error message.
 * @param error the error that was caught from Axios
 * @returns the error to be thrown
 */
export const getErrorWithMessage = (error: any): CuredApiError => {
    const userFriendlyErrorMessageFromAPI: string | undefined = error.response?.data.message;
    const errorCode: string | undefined = error.response?.data.errorCode;
    // Prefer FE-defined message for known error codes, fall back to BE message
    const errorMessage: string = ErrorCodes[errorCode] ?? userFriendlyErrorMessageFromAPI ?? ErrorCodes.DEFAULT;
    const err = new Error(errorMessage) as CuredApiError;
    if (errorCode) {
        err.errorCode = errorCode;
    }
    return err;
};

/**
 * Gets the audience label for a select.
 * @param audience the audience for which the label will be returned
 * @param channel the channel associated with the audience, if applicable
 * @param count the audience count
 * @param isError isError from the useAudienceCount hook
 * @param isLoading isLoading or isFetching from the useAudienceCount hook
 *
 * @param selectedAudienceId the selected audience id (counts will only be shown for this audience)
 * @returns the audience label with the count if it is selected
 */
type Args = {
    audience: Partial<Segment | Cohort>;
    channel?: string;
    count?: string | number;
    isError: boolean;
    isLoading: boolean;
    selectedAudienceId: number | string;
};

export const getAudienceLabel = ({
    audience,
    channel,
    count,
    isError,
    isLoading,
    selectedAudienceId,
}: Args): string => {
    if (count === undefined || !audience?.name || audience.id !== selectedAudienceId || isError || isLoading) {
        return audience?.name || '';
    }

    const countAsNum = Number(count);
    const maxLength = 39;

    const getChannelLabel = () => {
        if (!channel) return '';
        if (channel === CampaignChannels.SMS) return 'text message ';
        return `${channel.toLowerCase()} `;
    };

    const audienceCountText = `(${getNumberWithCommas(countAsNum)} ${getChannelLabel()}${
        countAsNum === 1 ? 'contact' : 'contacts'
    })`;

    const fullValueLength = audience.name.length + audienceCountText.length + 1;
    const isTruncationRequired = channel && fullValueLength > maxLength;

    const audienceName = isTruncationRequired
        ? audience.name.substring(0, maxLength - audienceCountText.length - 4) + '...'
        : audience.name;

    return `${audienceName} ${audienceCountText}`;
};

/**
 * Converts a snake case column name to a more friendly format.
 * For example, column_name -> column name.
 * @param name The snake case column name.
 * @returns The formatted friendly name.
 */
export const getFriendlyName = (name: string): string => name.replace(/_/g, ' ');

/**
 * Converts contact columns to merge tags.
 * @param contactColumns The contact column types.
 * @returns The merge tags.
 */
export const getMergeTags = (contactColumns: ContactColumn[]): MergeTag[] => {
    const supportedContactColumns = contactColumns.filter((contactColumn) => contactColumn.isMergeTag);

    return supportedContactColumns.map(
        ({ columnName, personalizationPreview }: ContactColumn): MergeTag => ({
            name: columnName,
            value: `{{${columnName}}}`,
            previewValue: personalizationPreview ?? '',
        })
    );
};

export const getUnlayerMergeTags = (contactColumns: ContactColumn[]) => {
    const supportedContactColumns = contactColumns.filter((contactColumn) => contactColumn.isMergeTag);

    const mergeTagsArray = supportedContactColumns.map(
        ({ columnName, personalizationPreview }: ContactColumn): MergeTag => ({
            name: getFriendlyName(columnName),
            value: `{{${columnName}}}`,
            previewValue: personalizationPreview ?? '',
        })
    );

    return mergeTagsArray.reduce((obj, item) => ({ ...obj, [item.name]: item }), {});
};

export const getPercentageFromRate = (rate: number): number =>
    Big(rate ?? 0)
        .times(100)
        .toNumber();

export const getCamelToKebabCase = (str: string): string =>
    str
        .replace(/([a-z0-9]|(?=[A-Z]))([A-Z])/g, '$1-$2')
        .toLowerCase()
        .replace(/^-/, '');

export const getMessageWithMergeTags = (mergeTags: MergeTag[], message: string) => {
    const regex = /{{.*?}}/g;
    const matches = message?.match(regex);

    if (matches) {
        let newContent: string = message;

        for (const match of matches) {
            const previewValue = mergeTags?.find((tag: MergeTag) => tag.value === match)?.previewValue;
            newContent = newContent.replaceAll(match, previewValue);
        }

        return newContent;
    }
    return message;
};

export const formatPhoneNumber = (phoneNumber: string): string => {
    if (!phoneNumber) return '';

    const digits = phoneNumber.replace(/\D/g, '');

    if (digits.length === 11 && digits.startsWith('1')) {
        const area = digits.slice(1, 4);
        const prefix = digits.slice(4, 7);
        const line = digits.slice(7, 11);
        return `+1 (${area}) ${prefix}-${line}`;
    }

    if (digits.length === 10) {
        const area = digits.slice(0, 3);
        const prefix = digits.slice(3, 6);
        const line = digits.slice(6, 10);
        return `(${area}) ${prefix}-${line}`;
    }

    return phoneNumber;
};

// --- Access Centre (list defaults, role detail parsing, timestamps) — see access-centre-api.md ---

/** Default page size for users and roles list endpoints (GET /users, GET /roles). */
export const ACCESS_CENTRE_LIST_PAGE_SIZE = 10;

/** Rows-per-page options for users and roles tables (matches Contacts table). */
export const ACCESS_CENTRE_ROWS_PER_PAGE_OPTIONS = [5, 10, 20, 50, 100];

export type AccessCentreRoleDetailWithPermissions = Omit<AccessCentreRole, 'user_count'> & {
    user_count?: number;
    permissions?: AccessCentrePermission[];
};

/**
 * GET /access-centre/roles/:id may return `{ data: { role } }` or a top-level `{ role }` depending on API wiring.
 * Use this when reading permissions for duplicate-role flows.
 */
export function getRoleDetailFromByIdResponse(body: unknown): AccessCentreRoleDetailWithPermissions | undefined {
    if (!body || typeof body !== 'object') return undefined;
    const b = body as {
        data?: { role?: AccessCentreRoleDetailWithPermissions };
        role?: AccessCentreRoleDetailWithPermissions;
    };
    return b.data?.role ?? b.role;
}

/** Local part of an email (substring before `@`), or trimmed string when `@` is missing. */
export const getEmailLocalPart = (email: string | null | undefined): string => {
    const t = (email ?? '').trim();
    const at = t.indexOf('@');
    if (at <= 0) {
        return t;
    }
    return t.slice(0, at);
};

/**
 * Label for Access Centre user name when the stored name is empty or equals the email
 * (common with IdP defaults). Shows the email local part so the column is readable.
 */
export const getAccessCentreUserDisplayLabel = (
    name: string | null | undefined,
    email: string | null | undefined
): string => {
    const trimmedName = (name ?? '').trim();
    const trimmedEmail = (email ?? '').trim();
    const local = getEmailLocalPart(trimmedEmail);

    if (trimmedName.length === 0) {
        return local;
    }
    if (trimmedEmail.length > 0 && trimmedName.toLowerCase() === trimmedEmail.toLowerCase()) {
        return local;
    }
    return trimmedName;
};

/**
 * Whether saving `trimmedDraft` would be a no-op vs current `name` / `email`
 * (including when the UI shows the email local part but the API still has empty / email as name).
 */
export const shouldSkipAccessCentreNamePatch = (
    trimmedDraft: string,
    name: string | null | undefined,
    email: string | null | undefined
): boolean => {
    const stored = (name ?? '').trim();
    if (trimmedDraft === stored) {
        return true;
    }
    const trimmedEmail = (email ?? '').trim();
    const local = getEmailLocalPart(trimmedEmail);
    if (stored === '' && trimmedDraft === local) {
        return true;
    }
    if (trimmedEmail.length > 0 && stored.toLowerCase() === trimmedEmail.toLowerCase() && trimmedDraft === local) {
        return true;
    }
    return false;
};

/**
 * Format ISO timestamp for settings tables: "Feb 15, 2026 at 10:30 AM"
 */
export const formatTimestamp = (isoTimestamp: string | null | undefined): string => {
    if (!isoTimestamp) return '—';
    const dt = DateTime.fromISO(isoTimestamp);
    if (!dt.isValid) return '—';
    return dt.toFormat("MMM d, yyyy 'at' h:mm a");
};

// --- Access Centre role builder (permission dependency graph) ---

/** Human-readable module name (e.g. "access management" / "content" → title-style label). */
export const formatModuleName = (moduleKey: string): string => {
    if (!moduleKey) return moduleKey;
    const withSpaces = moduleKey.replace(/_/g, ' ');
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1).toLowerCase();
};

/** Group permissions by module name. */
export const groupPermissionsByModule = (
    permissions: AccessCentrePermission[]
): Map<string, AccessCentrePermission[]> => {
    const map = new Map<string, AccessCentrePermission[]>();
    for (const p of permissions) {
        const list = map.get(p.module) ?? [];
        list.push(p);
        map.set(p.module, list);
    }
    return map;
};

/**
 * Unique `module` values in **API array order** (first occurrence wins). Matches backend `ORDER BY`
 * for GET permissions; do not sort alphabetically here.
 */
export const getPermissionModuleKeysInOrder = (permissions: AccessCentrePermission[]): string[] => {
    const seen = new Set<string>();
    const keys: string[] = [];
    for (const p of permissions) {
        if (!seen.has(p.module)) {
            seen.add(p.module);
            keys.push(p.module);
        }
    }
    return keys;
};

/**
 * Returns all permission ids that are transitive dependencies of the given permission
 * (e.g. if C depends on B and B depends on A, returns [A, B] for C).
 */
export const getTransitiveDependencyIds = (permissions: AccessCentrePermission[], permissionId: number): number[] => {
    const byKey = new Map(permissions.map((p) => [p.key, p]));
    const perm = permissions.find((p) => p.id === permissionId);
    if (!perm?.depends_on?.length) return [];
    const keysToAdd = new Set<string>();
    const queue = [...perm.depends_on];
    while (queue.length > 0) {
        const key = queue.shift()!;
        if (keysToAdd.has(key)) continue;
        keysToAdd.add(key);
        const p = byKey.get(key);
        if (p?.depends_on?.length) {
            for (const k of p.depends_on) {
                if (!keysToAdd.has(k)) queue.push(k);
            }
        }
    }
    return Array.from(keysToAdd)
        .map((key) => byKey.get(key)?.id)
        .filter((id): id is number => id !== undefined);
};

/**
 * When one or more permissions are turned off, also turns off any selected permission whose
 * transitive prerequisites include any of those ids (fixed-point over the dependency graph).
 * Example: A depends on B, B depends on C — disabling C removes B and A when selected.
 */
export const appendQueryParam = (url: string, key: string, value: string): string => {
    const [path, search] = url.split('?');
    const params = new URLSearchParams(search);
    params.set(key, value);
    return `${path}?${params.toString()}`;
};

export const getCascadeRemovedPermissionIds = (
    permissions: AccessCentrePermission[],
    currentlySelected: Set<number>,
    initialRemovedIds: Iterable<number>
): Set<number> => {
    const toRemove = new Set(initialRemovedIds);
    let changed = true;
    while (changed) {
        changed = false;
        for (const pId of Array.from(currentlySelected)) {
            if (toRemove.has(pId)) continue;
            const deps = getTransitiveDependencyIds(permissions, pId);
            if (deps.some((d) => toRemove.has(d))) {
                toRemove.add(pId);
                changed = true;
            }
        }
    }
    return toRemove;
};
